import { type Partida, sumaUsd } from "@/componentes/resumen/tipos"
import { monto } from "@/componentes/tesoreria/dinero"
import { Insignia, type Tono } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha } from "@/lib/fechas"

/** Qué es cada partida, en palabras. Lo de la izquierda lo pone el servidor. */
export const CLASE: Record<string, string> = {
  CXP_OPERATIVA: "Operativa (2.0)",
  CXP_ADMINISTRATIVA: "Administrativa (2.0)",
  CXP_3_0: "CxP del 3.0",
  ORDEN_PAGO: "Orden de pago",
  PROGRAMADO_GASTO: "Gasto programado",
  PROGRAMADO_NOMINA: "Nómina",
  SIN_IDENTIFICAR: "Sin identificar",
}

const TONO: Record<string, Tono> = {
  POR_PAGAR: "aviso",
  ABONADA: "aviso",
  ORDEN_BORRADOR: "neutro",
  ORDEN_EN_APROBACION: "info",
  POR_EJECUTAR: "exito",
  PROYECTADA: "neutro",
  EXIGIBLE: "exito",
  VALIJA: "info",
  EN_CURSO: "neutro",
  COBRADA_2: "neutro",
}

const usd = (v: string | null) => (v === null ? "sin tasa" : `$ ${monto(v)}`)

/**
 * Una lista agrupada por `campo` («estado» de las CxP o «clase» de las CxC), en el orden
 * de `grupos`, cada grupo con su subtotal. Lo que no suma va atenuado y lo dice su rótulo.
 */
export function ListaAgrupada({
  partidas,
  grupos,
  campo,
  conOrden = false,
  vacia,
  onAbrir,
}: {
  partidas: Partida[]
  grupos: Record<string, string>
  campo: "estado" | "clase"
  conOrden?: boolean
  vacia: string
  /** Abre el detalle de la partida. Sin él, la lista sólo se lee (la foto pública). */
  onAbrir?: (p: Partida) => void
}) {
  const conPartidas = Object.keys(grupos)
    .map((g) => [g, partidas.filter((p) => (p[campo] ?? "") === g)] as const)
    .filter(([, ps]) => ps.length > 0)
  if (conPartidas.length === 0) return <p className="tarjeta p-4 text-sm">{vacia}</p>
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 text-xs">
        {conPartidas.map(([g, ps]) => (
          <a key={g} href={`#grupo-${g}`} className="tarjeta px-3 py-1.5 tabular-nums">
            {grupos[g]}: {ps.length} · $ {monto(sumaUsd(ps).toFixed(2))}
          </a>
        ))}
      </div>
      {conPartidas.map(([g, ps]) => (
        <div key={g} id={`grupo-${g}`} className="space-y-1">
          <h3 className="flex flex-wrap items-center justify-between gap-2 text-sm font-semibold">
            <Insignia tono={TONO[g] ?? "neutro"}>{grupos[g]}</Insignia>
            <span className="tabular-nums">
              {ps.length} · $ {monto(sumaUsd(ps).toFixed(2))}
            </span>
          </h3>
          <Tabla etiqueta={grupos[g] ?? g}>
            <Cabecera>
              <Th>Quién y qué</Th>
              <Th>Expediente</Th>
              {conOrden && <Th>Orden</Th>}
              <Th>Vence</Th>
              <Th derecha>Monto</Th>
              <Th derecha>USD</Th>
            </Cabecera>
            <Cuerpo>
              {ps.map((p, i) => (
                <Fila
                  key={`${p.clase}-${p.ref_id ?? i}`}
                  atenuada={!p.suma}
                  className={onAbrir && p.ref_id !== null ? "cursor-pointer" : undefined}
                  onClick={(e) => {
                    if (!onAbrir || p.ref_id === null) return
                    if (!(e.target as HTMLElement).closest("button")) onAbrir(p)
                  }}
                >
                  <Td>
                    {onAbrir && p.ref_id !== null ? (
                      <button
                        type="button"
                        className="foco-cristal text-left hover:underline"
                        aria-label={`Ver el detalle de ${p.titulo}`}
                        onClick={() => onAbrir(p)}
                      >
                        {p.titulo}
                      </button>
                    ) : (
                      p.titulo
                    )}
                    <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                      {CLASE[p.clase] ? `${CLASE[p.clase]} · ` : ""}
                      {p.detalle}
                      {p.nota ? ` · ${p.nota}` : ""}
                    </span>
                  </Td>
                  <Td numero>{p.expediente ?? "—"}</Td>
                  {conOrden && <Td numero>{p.orden ?? "—"}</Td>}
                  <Td numero>{p.fecha ? comoFecha(p.fecha) : "—"}</Td>
                  <Td derecha numero>
                    {monto(p.monto)} {p.moneda}
                  </Td>
                  <Td derecha numero>
                    {usd(p.usd)}
                  </Td>
                </Fila>
              ))}
            </Cuerpo>
          </Tabla>
        </div>
      ))}
    </div>
  )
}

export function ListaGeneral({ partidas, etiqueta }: { partidas: Partida[]; etiqueta: string }) {
  return (
    <Tabla etiqueta={etiqueta}>
      <Cabecera>
        <Th>Qué</Th>
        <Th>Fecha</Th>
        <Th derecha>Monto</Th>
        <Th derecha>USD</Th>
      </Cabecera>
      <Cuerpo>
        {partidas.length === 0 && <TablaVacia colSpan={4}>Nada aquí.</TablaVacia>}
        {partidas.map((p, i) => (
          <Fila key={`${p.clase}-${p.ref_id ?? i}-${p.titulo}`} atenuada={!p.suma}>
            <Td>
              {p.titulo}
              <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                {CLASE[p.clase] ? `${CLASE[p.clase]} · ` : ""}
                {p.detalle}
                {p.nota ? ` · ${p.nota}` : ""}
              </span>
            </Td>
            <Td numero>{p.fecha ? comoFecha(p.fecha) : "—"}</Td>
            <Td derecha numero>
              {monto(p.monto)} {p.moneda}
            </Td>
            <Td derecha numero>
              {usd(p.usd)}
            </Td>
          </Fila>
        ))}
      </Cuerpo>
    </Tabla>
  )
}
